import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
} from "react-router";
import { QueryClientProvider } from '@tanstack/react-query';

import type { Route } from "./+types/root";
import { queryClient } from "./lib/queryClient";
import { AppLayout } from "./components/layout";
import { useGuestMode } from "./contexts/GuestModeContext";
import { OverlayLoader } from "./components/ui/overlay-loader";
import { HeaderProvider } from "./contexts/HeaderContext";
import { LoadingProvider } from "./contexts/LoadingContext";
import { GuestModeProvider } from "./contexts/GuestModeContext";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import { ThemeProvider } from "./lib/theming";
import { Toaster } from "./components/ui/sonner";
import "./lib/i18n/config"; // Initialize i18n
import "./app.css";

export const links: Route.LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap",
  },
  // PWA Manifest
  { rel: "manifest", href: "/manifest.json" },
];

function LayoutContent({ children }: { children: React.ReactNode }) {
  const { language, direction } = useLanguage();
  
  return (
    <html lang={language} dir={direction}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, minimum-scale=1, user-scalable=no, viewport-fit=cover, shrink-to-fit=no" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        
        {/* PWA Meta Tags for Mobile App Experience */}
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-title" content="WhenCanIQuit" />
        <meta name="application-name" content="WhenCanIQuit" />
        <meta name="theme-color" content="#3182ce" />
        <meta name="msapplication-navbutton-color" content="#3182ce" />
        
        {/* Prevent zoom on input focus (iOS) and disable double-tap delay */}
        <meta name="format-detection" content="telephone=no" />
        <meta name="msapplication-tap-highlight" content="no" />
        
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <LanguageProvider>
      <LayoutContent>{children}</LayoutContent>
    </LanguageProvider>
  );
}

function AppContent() {
  const location = useLocation();
  const { isGuestDbReady } = useGuestMode();

  // Show loading only during guest mode initialization
  if (!isGuestDbReady) {
    return <OverlayLoader message="Initializing app..." />;
  }

  // Always render content - no authentication needed
  return (
    <AppLayout>
      <Outlet />
    </AppLayout>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <LanguageProvider>
          <GuestModeProvider>
            <LoadingProvider>
              <HeaderProvider>
                <AppContent />
                <Toaster />
              </HeaderProvider>
            </LoadingProvider>
          </GuestModeProvider>
        </LanguageProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <main className="pt-16 p-4 container mx-auto">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full p-4 overflow-x-auto">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
